import { useEffect, useState } from 'react'

const STORAGE_KEY = 'sidebar-collapsed'

/**
 * Routes that render the bare minimal rail instead of the full sidebar.
 *
 * Thread detail (`/threads/:token`) USED to opt in, on the rationale that it is
 * a focused reading view. Removed 2026-08-29: opening a thread is the most
 * common navigation in the app, and force-collapsing on entry meant the rail
 * the user had deliberately expanded kept disappearing under them — the
 * sidebar's own Recent list navigates here, so the act of using the rail hid
 * the rail. A reading view is not worth a nav that will not hold still. The
 * study report (`/tests/:id/results`) needs no entry here: it renders outside
 * the app shell entirely and has no sidebar to collapse.
 */
function isMinimalChromePath(pathname: string): boolean {
  if (pathname === '/tests/new' || pathname.startsWith('/tests/new/')) {
    return true
  }
  // A chat room (`/chats/:token`) is a stage of its own, from the empty-room
  // cover flow through the whole conversation: the rail stays a bare expand
  // glyph (Fernando, 2026-10-14). The rooms list (`/chats`) keeps the full sidebar.
  return pathname.startsWith('/chats/')
}

function shouldForceCollapse(pathname: string) {
  return isMinimalChromePath(pathname)
}

/**
 * Sidebar / mobile-nav client state shared by Next `AppShell` and Vite `SpaAppShellLayout`.
 *
 * The sidebar starts open by default. The user can collapse it and their
 * preference is persisted to localStorage across navigations and sessions.
 * The questionnaire builder route auto-collapses on entry without overwriting
 * the persisted preference.
 */
export function useAppShellChromeLayout(pathname: string) {
  const [mounted, setMounted] = useState(false)
  const [isCollapsed, setIsCollapsed] = useState(false)
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const isMinimalChrome = isMinimalChromePath(pathname)

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (shouldForceCollapse(pathname)) {
      setIsCollapsed(true)
    } else if (stored !== null) {
      setIsCollapsed(stored === 'true')
    }
    setMounted(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setMobileNavOpen(false)
    if (shouldForceCollapse(pathname)) {
      setIsCollapsed(true)
    } else {
      const stored = localStorage.getItem(STORAGE_KEY)
      setIsCollapsed(stored === 'true')
    }
  }, [pathname])

  function toggleSidebar() {
    setIsCollapsed((prev) => {
      const next = !prev
      // Don't persist manual toggles made on auto-collapse / minimal-chrome
      // routes — those pages force-collapse on entry and shouldn't overwrite
      // the global preference.
      if (!shouldForceCollapse(pathname)) {
        localStorage.setItem(STORAGE_KEY, String(next))
      }
      return next
    })
  }

  return {
    mounted,
    isCollapsed,
    mobileNavOpen,
    setMobileNavOpen,
    toggleSidebar,
    isMinimalChrome,
  }
}
